import { zodResolver } from '@hookform/resolvers/zod'
import { Box } from '@mui/material'
import React, { memo, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { useNotificationStack } from '../../../app/hooks/useNotificationStack'
import SubmitButtons from '../../../components/Buttons/SubmitButtons'
import { useConfirmDialogContext } from '../../../components/ConfirmDialog'
import TabWrapper from '../../../components/TabWrapper/TabWrapper'
import { useSideModalContext } from '../../Assets/modals/SideModalContext'
import Temperature from '../ProductTab/ProductForm/Temperature'
import { IProductCategory } from '../types'
import { useHsCode } from './hooks/useHsCode'
import { useProductCategory } from './hooks/useProductCategory'
import HsCodeSelector from './HsCodeSelector'
import HsLocalTariffTable from './HsLocalTariffTable'
import MainInfo from './MainInfo'
import messages from './messages'
import { getProductCategoryValidationSchema } from './productCategoryValidationSchemas'

interface ProductCategoryFormProps {
  readonly productCategory: IProductCategory
  readonly isDisabled: boolean
}

const ProductCategoryForm: React.FC<ProductCategoryFormProps> = memo(
  ({ productCategory, isDisabled }) => {
    const isNew = !productCategory.id
    const { onClose } = useSideModalContext()
    const { formatMessage } = useIntl()
    const { confirm } = useConfirmDialogContext()
    const { enqueueSuccess, enqueueFailure } = useNotificationStack()

    const { createProductCategory, updateProductCategory } = useProductCategory({
      customerId: productCategory.customerId ?? -1,
    })

    const { hsHeadingItems, hsSubheadingItems } = useHsCode({ skip: isDisabled })

    const methods = useForm<IProductCategory>({
      defaultValues: productCategory,
      resolver: zodResolver(getProductCategoryValidationSchema(formatMessage)),
      mode: 'onChange',
    })

    const { handleSubmit } = methods

    const handleFormSubmit = useCallback(
      async (values: IProductCategory) => {
        try {
          if (!values.id) {
            const confirmed = await confirm({
              title: formatMessage(messages.createProductDialogTitle),
              message: formatMessage(messages.createProductDialogText),
              confirmText: 'Create',
              cancelText: 'Cancel',
              severity: 'warning',
              confirmButtonColor: 'primary',
              cancelButtonColor: 'secondary',
            })

            if (confirmed) {
              await createProductCategory(values)
                .then(() => {
                  enqueueSuccess(messages.productCategoryCreated)
                  onClose()
                })
                .catch(() => {
                  enqueueFailure(messages.productCategoryNotCreated)
                })
            }
          } else {
            await updateProductCategory(values)
              .then(() => {
                enqueueSuccess(messages.productCategoryUpdated)
                onClose()
              })
              .catch(() => {
                enqueueFailure(messages.productCategoryNotUpdated)
              })
          }
        } catch (error) {
          console.error('Error submitting form:', error)
          enqueueFailure(messages.productCategoryNotCreated)
        }
      },
      [confirm, formatMessage, createProductCategory, updateProductCategory, onClose, enqueueSuccess, enqueueFailure]
    )

    const onSubmitHandler = handleSubmit(handleFormSubmit)

    const tabsConfig = [
      {
        label: formatMessage(messages.hsCodeDetailsTab),
        content: (
          <>
            <HsCodeSelector
              hsHeadingItems={hsHeadingItems}
              hsSubheadingItems={hsSubheadingItems}
              isDisabled={isDisabled}
              isEdit={!isNew}
            />
            <HsLocalTariffTable isDisabled={isDisabled} />
          </>
        ),
      },
      {
        label: formatMessage(messages.temperatureTab),
        content: <Temperature isDisabled={isDisabled} showOverrideToggle={false} />,
      },
    ]

    return (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Box sx={{ m: '32px' }}>
            <MainInfo isDisabled={isDisabled} isEdit={!isNew} />
          </Box>
          <Box sx={{ m: '32px' }}>
            <TabWrapper tabs={tabsConfig} ariaLabel='product-category-tabs' />
          </Box>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              padding: '16px',
              backgroundColor: '#fff',
              borderTop: '1px solid #e0e0e0',
              zIndex: 999,
            }}
          >
            {!isDisabled && (
              <SubmitButtons create={isNew} onCancel={onClose} submitForm={onSubmitHandler} />
            )}
          </Box>
        </form>
      </FormProvider>
    )
  }
)

ProductCategoryForm.displayName = 'ProductCategoryForm'

export default ProductCategoryForm
